import { openAsk, type ChatTurn } from "./openAsk";

const APP_HELP: Array<{ test: RegExp; answer: string }> = [
  {
    test: /plan variado|trazo un plan|otro sitio|otra ubicaci/i,
    answer:
      "Cambia a ubicación manual, pon la ciudad o arrastra el pin, escribe tu tiempo libre (horas y minutos), tu presupuesto en euros y cómo te mueves (a pie, bici o coche). Luego pulsa «Buscar planes»: Hermes combina tus hobbies, el filtro de compañía y los sitios de ese radio.",
  },
  {
    test: /(c[oó]mo|d[oó]nde|poner|pongo|cambi|escrib).{0,40}(presupuesto|tiempo libre|precio)/i,
    answer:
      "En Planes escribe tu tiempo libre en horas y minutos (incluye ir y volver) y el presupuesto por persona en euros: vacío es sin límite y 0 es solo gratis. Los precios son orientativos. Hermes no busca hasta que pulsas «Buscar planes».",
  },
  {
    test: /ubicaci[oó]n|otra ciudad|manual|direcci[oó]n/i,
    answer:
      "En Planes puedes usar la ubicación actual o una manual. En manual escribe país, provincia, población, código postal, calle y número, o arrastra el pin del mapa. La búsqueda de sitios usa ese punto, no el GPS.",
  },
  {
    test: /hobb(y|ies|is)|perfil|material/i,
    answer: "En el perfil guarda hobbies y objetos. Pulsa Guardar: Hermes los usa para proponer planes que encajan contigo.",
  },
  {
    test: /google|entrar|cuenta/i,
    answer: "Puedes entrar con correo o con Continuar con Google.",
  },
];

function lastTopic(history: ChatTurn[]): string {
  const blob = history
    .slice(-6)
    .map((t) => t.text)
    .join(" ")
    .toLowerCase();
  if (/dibuj/.test(blob)) return "dibujar";
  if (/leer|libro|lectura/.test(blob)) return "leer";
  if (/cocin|receta/.test(blob)) return "cocinar";
  if (/viaj|escapada|ruta|paseo/.test(blob)) return "viajar";
  if (/ver|serie|pel[ií]cula|cine/.test(blob)) return "ver";
  if (/hacer|plan|aburr/.test(blob)) return "hacer";
  return "";
}

function creativeFallback(question: string, history: ChatTurn[]): string {
  const q = question.toLowerCase();
  const topic = lastTopic(history);
  const follow =
    /^(y\s+)?(qu[eé]|cual|cu[aá]l|dame|m[aá]s|otro|otra|ejempl)/i.test(question.trim()) ||
    /qu[eé]\s+(dibuj|leo|leer|cocin|hago|ver|visito)/i.test(q);

  if (follow && (topic === "dibujar" || /dibuj/.test(q))) {
    return [
      "Ideas concretas para dibujar ahora:",
      "1. Una taza vista desde arriba con el asa torcida.",
      "2. Tu mano en tres gestos (puño, abierta, señalando).",
      "3. La ventana de la habitación con lo que se ve fuera.",
      "4. Un personaje inventado en 5 líneas (silueta rápida).",
      "5. Un objeto de tu mesa con sombra dura.",
      "Empieza por el 1 o el 2: 10 minutos, sin borrar.",
    ].join("\n");
  }
  if (follow && (topic === "leer" || /leer|libro|\bleo\b/.test(q))) {
    return [
      "Para leer ahora, según humor:",
      "1. Un cuento corto (20–30 min) de un autor que ya te guste.",
      "2. Un capítulo de no-ficción práctica (hábitos, viajes, oficio).",
      "3. Poesía o ensayos breves si quieres densidad sin páginas.",
      "4. Relee 10 páginas de un libro a medias en vez de empezar otro.",
      "Si me dices género o idioma, te afino la lista.",
    ].join("\n");
  }
  if (follow && (topic === "cocinar" || /cocin|receta/.test(q))) {
    return [
      "Platos rápidos con poco lío:",
      "1. Tortilla francesa con lo que haya en la nevera.",
      "2. Pasta aglio e olio + proteína (huevo, atún o garbanzos).",
      "3. Bowl: base (arroz/patata) + verdura salteada + salsa yogur/limón.",
      "4. Sándwich tostado con queso y algo crujiente.",
      "Dime qué tienes en casa y te armo la receta exacta.",
    ].join("\n");
  }
  if (follow && (topic === "viajar" || /viaj|d[oó]nde ir|ruta/.test(q))) {
    return [
      "Planes de salida concretos:",
      "1. Paseo de 45–90 min por un barrio que no uses a diario + café al final.",
      "2. Mirador o parque alto al atardecer.",
      "3. Museo o exposición pequeña (máx. 2 horas).",
      "4. Ruta a pie entre dos puntos conocidos con un desvío nuevo.",
      "Abre Planes en Hermes con tu ubicación (o manual) y genera sitios del radio.",
    ].join("\n");
  }
  if (/qu[eé]\s+hacer|aburr|ideas|propon|sugiere|plan/i.test(q) || topic === "hacer") {
    return [
      "Algunas ideas ahora mismo:",
      "• Dibujar (objeto de la mesa, 10 min).",
      "• Leer 15 páginas o un cuento corto.",
      "• Cocinar algo simple o preparar mañana el almuerzo.",
      "• Salir a caminar 30–60 min sin móvil.",
      "• Ver un documental corto o un capítulo.",
      "• Ordenar un cajón o una carpeta digital.",
      "Dime cuál te atrae («y qué dibujo», «qué leo»…) y te doy opciones concretas.",
    ].join("\n");
  }
  if (/dibuj/.test(q)) {
    return creativeFallback("qué dibujo", [...history, { role: "assistant", text: "Dibujar" }]);
  }
  if (/leer|libro|\bleo\b/.test(q)) {
    return creativeFallback("qué leo", [...history, { role: "assistant", text: "Leer" }]);
  }
  return "Pregúntame qué hacer, dónde ir, qué dibujar, qué leer o cómo usar Planes y la ubicación. Cuanto más concreto seas, mejor te respondo.";
}

export async function answerHermes(
  question: string,
  context?: { place?: string; minutes?: number; hobbies?: string[] },
  history: ChatTurn[] = [],
): Promise<string> {
  const raw = question.trim();
  for (const item of APP_HELP) {
    if (item.test.test(raw) && !/qu[eé]\s+hacer|aburr|dibuj|leer|cocin/i.test(raw)) {
      return item.answer;
    }
  }

  if (context?.place && /ma[nñ]ana|d[oó]nde|donde|qu[eé] hacer|ir\b/i.test(raw) && !/dibuj|leer|cocin/i.test(raw)) {
    const taste = context.hobbies?.length
      ? ` Con lo que te gusta (${context.hobbies.slice(0, 4).join(", ")}), abre`
      : " Abre";
    return `Mañana, alrededor de ${context.place}, mira planes de unos ${context.minutes || 90} minutos.${taste} Planes con esa ubicación y genera: prioriza un sitio exterior y otro cubierto por si cambia el tiempo.`;
  }

  const placeHint = context?.place
    ? `Ubicación actual del usuario: ${context.place}. Tiempo libre típico: ${context.minutes || 90} minutos. Hobbies: ${(context.hobbies || []).join(", ") || "no indicados"}.`
    : "Si habla de planes locales, sugiere usar la pestaña Planes de Hermes con ubicación actual o manual.";

  return openAsk({
    appName: "Hermes",
    system: `${placeHint} Hermes sirve para descubrir planes, rutas y sitios según ubicación, tiempo libre y hobbies.`,
    question: raw,
    history,
    fallback: creativeFallback,
  });
}
